import { Coverage, VehicleCoverage } from '@aaa-ncnu-ie/mypolicy-api-policies';
import { Box, Stack, Typography } from '@mui/material';

import ChevronDownIcon from '../../../../assets/icons/ChevronDown';
import ChevronUpIcon from '../../../../assets/icons/ChevronUp';
import InfoIcon from '../../../../assets/icons/InfoIconBorderFill';
import ExpandableList from '../../../../components/ExpandableList';
import colors from '../../../../constants/colors';
import useAnalytics from '../../../../hooks/useAnalytics';
import useSortedCoverages, {
  NO_COVERAGE,
} from '../../../../hooks/useSortedCoverages';
import { handleForwardSlash, shouldShowCoverageText } from '../../helper';

interface PolicyCoverageDetailsProps {
  policyCoverages: Coverage[];
  vehicleCoverages: VehicleCoverage[];
  policyNumber?: string;
}

const PolicyCoverageDetails = ({
  policyCoverages,
  vehicleCoverages,
  policyNumber,
}: PolicyCoverageDetailsProps) => {
  const { track } = useAnalytics();
  const sortedCoverages = useSortedCoverages(policyCoverages);

  const waivedLiabilityVehicles = vehicleCoverages.filter(
    (coverage) => coverage.waivedLiabilityInd
  );
  const content = (isExpanded: boolean) => (
    <Stack
      direction="row"
      sx={{
        justifyContent: "space-between",
        p: 2,
        width: 1
      }}>
      <Typography
        variant="h3"
        data-testid="policyCoverageMainCard"
        sx={{
          fontWeight: 700,
          fontSize: { mobile: 16, tablet: 18 },
          lineHeight: '24px',
          color: colors.csaaBlack,
          letterSpacing: { mobile: 0.3, tablet: 0.4 },
          textAlign: 'left',
        }}
      >
        Liability coverages
      </Typography>
      <Stack
        sx={{
          background: 'rgba(11, 20, 33, 0.1)',
          cursor: 'pointer',
          p: 0.5,
          height: 'fit-content',
          borderRadius: 1,
        }}
      >
        {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Stack>
    </Stack>
  );

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: colors.csaaBorder,
        borderRadius: 2,
      }}
    >
      <ExpandableList
        content={content}
        id="auto-policy"
        onClick={(isOpen) =>
          track('Auto Policy Details Toggled', {
            event_type: 'UX Modified',
            location: 'Feature Card',
            link: 'Liability coverages',
            selection: isOpen ? 'Closed' : 'Opened',
            policy_number: policyNumber,
          })
        }
      >
        <Stack
          data-testid="coverageDetailMainContainer"
          spacing={1}
          sx={{
            p: 2,
            backgroundColor: colors.csaaNeutralBackground,
            borderTop: '1px solid',
            borderColor: colors.csaaBorder,
            borderBottomRightRadius: 8,
            borderBottomLeftRadius: 8,
          }}
        >
          {waivedLiabilityVehicles.map((vehicle, idx) => (
            <Stack
              key={`${vehicle.vin}-${idx}`}
              direction="row"
              sx={{
                alignItems: "center",
                backgroundColor: colors.csaaBorder,
                borderColor: colors.csaaBorder,
                borderRadius: 1,
                py: 1,
                px: { mobile: 1, tablet: 3 }
              }}>
              <InfoIcon />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontSize: { mobile: '16px', tablet: '18px' },
                  color: colors.csaaMid,
                  ml: 2,
                }}
              >
                {vehicle.year} {vehicle.make} {vehicle.series}
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    fontSize: { mobile: '16px', tablet: '18px' },
                    color: colors.csaaMid,
                    ml: 0.5,
                  }}
                  component="span"
                >
                  Liability coverages are waived and do not apply.
                </Typography>
              </Typography>
            </Stack>
          ))}
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              backgroundColor: colors.csaaBorder,
              borderColor: colors.csaaBorder,
              borderRadius: 1,
              py: 1,
              px: { mobile: 1, tablet: 3 }
            }}>
            <InfoIcon />

            <Typography
              sx={{
                fontWeight: 400,
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontSize: { mobile: '16px', tablet: '18px' },
                color: colors.csaaMid,
                ml: 2,
              }}
            >
              Coverage details available in policy declarations document (see
              MyPolicy document library).
            </Typography>
          </Stack>
          {sortedCoverages.map((item, idx) => {
            const hasCoverage =
              item.coverageLimit?.toLowerCase() !== NO_COVERAGE;
            return (
              <Stack
                key={`${item.coverageCd}-${idx}`}
                sx={{
                  backgroundColor: colors.csaaWhite,
                  p: { mobile: 2, tablet: 3 },
                  boxShadow: `inset 0px 0px 0px 1px ${colors.csaaNeutral}`,
                  borderRadius: 2,
                }}
              >
                <Stack direction="row" sx={{
                  justifyContent: "space-between"
                }}>
                  <Typography
                    sx={{
                      fontFamily: 'Cabin',
                      fontKerning: 'normal',
                      fontSize: { mobile: 16, tablet: 18 },
                      lineHeight: '24px',
                      fontWeight: 700,
                      letterSpacing: { mobile: 0.3, tablet: 0.4 },
                      color: colors.csaaBlack,
                    }}
                    data-testid={`coverage-text-${item.coverageCd}`}
                  >
                    {item.coverageDescription}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Cabin',
                      fontKerning: 'normal',
                      fontSize: { mobile: 16, tablet: 18 },
                      lineHeight: '24px',
                      fontWeight: 700,
                      letterSpacing: { mobile: 0.3, tablet: 0.4 },
                      whiteSpace: 'nowrap',
                      color: colors.csaaBlack,
                    }}
                    data-testid={`coverage-${
                      hasCoverage ? 'premium' : 'limit'
                    }-${item.coverageCd}`}
                  >
                    {hasCoverage ? item.coveragePremium : item.coverageLimit}
                  </Typography>
                </Stack>
                {hasCoverage && shouldShowCoverageText(item.coverageLimit) && (
                  <Stack direction="row">
                    <Typography
                      sx={{
                        fontFamily: 'Cabin',
                        fontKerning: 'normal',
                        fontWeight: 400,
                        fontSize: { mobile: 16, tablet: 18 },
                        lineHeight: '24px',
                        color: colors.csaaMid,
                        letterSpacing: { mobile: 0.3, tablet: 0.4 },
                        mt: 1,
                      }}
                      data-testid={`coverage-limit-${item.coverageCd}`}
                    >
                      {item.coverageLimit}
                    </Typography>
                  </Stack>
                )}
                {hasCoverage && shouldShowCoverageText(item.coverageType) && (
                  <Stack direction="row">
                    <Typography
                      sx={{
                        fontFamily: 'Cabin',
                        fontKerning: 'normal',
                        fontWeight: 400,
                        fontSize: { mobile: 14, tablet: 16 },
                        lineHeight: '24px',
                        color: colors.csaaMid,
                        letterSpacing: { mobile: 0.2, tablet: 0.3 },
                        mt: 1,
                      }}
                      data-testid={`coverage-type-${item.coverageCd}`}
                    >
                      {handleForwardSlash(item.coverageType!)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            );
          })}
        </Stack>
      </ExpandableList>
    </Box>
  );
};

export default PolicyCoverageDetails;
